import { useParams } from 'react-router-dom';
import { getDateSeed, verifyDateParam } from '../../lib/dateSeed';
import { parseDateString } from '../../lib/dailyPuzzle';
import { isDevelopment } from '../../lib/utils';
import { Game2048Game } from './Game2048Game';

const parseDifficulty = (value: string | undefined): number | null => {
  if (!value || !/^[1-4]$/.test(value)) {
    return null;
  }
  return Number(value);
};

interface Game2048RouteSettings {
  date: Date | null;
  difficulty: number | null;
  seed: number | undefined;
  isPreviewMode: boolean;
}

const emptyRouteSettings = (): Game2048RouteSettings => ({
  date: null,
  difficulty: null,
  seed: undefined,
  isPreviewMode: false
});

const parsePreview = (
  previewDate: string | undefined,
  previewDifficulty: string | undefined
): Game2048RouteSettings | null => {
  if (!isDevelopment() || previewDate === undefined) {
    return null;
  }

  const difficulty = parseDifficulty(previewDifficulty);
  if (difficulty === null) {
    return emptyRouteSettings();
  }

  try {
    const date = parseDateString(previewDate);
    return {
      date,
      difficulty,
      seed: getDateSeed(date, difficulty),
      isPreviewMode: true
    };
  } catch (_error) {
    return emptyRouteSettings();
  }
};

const parseSeed = (seed: string | undefined): Game2048RouteSettings => {
  if (seed === undefined) {
    return emptyRouteSettings();
  }

  const [date, difficulty] = verifyDateParam(seed);
  if (date === null || difficulty === null || ![1, 2, 3, 4].includes(difficulty)) {
    return emptyRouteSettings();
  }

  return { date, difficulty, seed: Number(seed), isPreviewMode: false };
};

const getRouteSettings = (
  seed: string | undefined,
  previewDate: string | undefined,
  previewDifficulty: string | undefined
): Game2048RouteSettings => {
  return parsePreview(previewDate, previewDifficulty) ?? parseSeed(seed);
};

export const Game2048 = () => {
  const { seed, previewDate, previewDifficulty } = useParams();
  const settings = getRouteSettings(seed, previewDate, previewDifficulty);

  return (
    <div className="game-2048-wrapper">
      <Game2048Game
        key={`${settings.isPreviewMode ? 'preview' : 'play'}:${settings.seed ?? 'today'}`}
        initialSeed={settings.seed}
        puzzleDate={settings.date}
        difficulty={settings.difficulty}
        isPreviewMode={settings.isPreviewMode}
      />
    </div>
  );
};

export default Game2048;
